import type { RecordFileItem } from '@ofmos/shared/files';
import { useState } from 'react';
import { useAppClient } from '../../data/ClientProvider';
import { cx, Spinner } from '../../ui';

/**
 * AN IMAGE PREVIEW, AND NOTHING ELSE PREVIEWS (`docs/decisions/file-attachments.md` D6).
 *
 * THE `<img>` POINTS AT THE ROUTE DIRECTLY. D5's whole argument arrives here: the API is
 * same-origin behind Caddy, so a subresource request carries the session cookie and the
 * permission check a signed URL exists to replace is already on it. Fetching every thumbnail
 * through JS into an object URL would cost a revocation lifecycle on every unmount for no
 * gain a user can see.
 *
 * NO SERVER-SIDE THUMBNAILS, and the cost is stated rather than discovered: a 40-pixel
 * preview downloads the full photo, so ten images on a record is the whole ten megabytes to
 * render ten thumbnails. `loading="lazy"` and D21's count cap are the mitigations; the fix,
 * if it is ever needed, is a `variant` column on the ledger and a second key under the same
 * driver (section 11).
 *
 * THE FALLBACK TILE IS THE POINT OF THE `onError`. A broken thumbnail is a tolerable failure
 * and a broken download is not, which is why the preview may be a bare `<img>` while the
 * download goes through the transport. When it does fail - a restore that brought back a
 * dump without its volume is the case D2 accepts - the tile says so in words instead of
 * showing the browser's own broken-image glyph.
 *
 * AND THE TILE IS FOR A CONFIRMED FILE ONLY (wave 1c, finding 3). The `<img>` used to be
 * rendered from the OPTIMISTIC value, so it asked for bytes before the PATCH had LINKED the
 * file - and `findLinkedRecordFile` correctly answers 404 for one that is not linked, after
 * which `onError` latched "This image could not be loaded." until the row remounted. That is
 * a lie about a file that is perfectly fine: it says the bytes are broken when the truth is
 * that the record write has not come back yet. So an UNCONFIRMED item renders the frame and
 * waits, and no request is made at all - which is also why there is no retry loop here. The
 * confirmation is the retry.
 */
export interface FilePreviewProps {
  readonly item: RecordFileItem;
  /** Tailwind sizing for the frame. The bounded record-page height, or the dialog's. */
  readonly className?: string | undefined;
  /**
   * The record write that attaches this file has not settled yet. The bytes are on the
   * server; the LINK that makes them readable is not, so there is nothing to ask for.
   */
  readonly pending?: boolean | undefined;
}

const TILE =
  'flex items-center justify-center rounded-control border border-line bg-surface-sunken px-3 py-2 text-xs text-ink-muted';

export function FilePreview({ item, className, pending = false }: FilePreviewProps) {
  const client = useAppClient();
  const [failed, setFailed] = useState(false);

  if (pending) {
    /**
     * The same frame, holding the same indeterminate spinner D18 gives an upload in flight -
     * because from the reader's side this IS still in flight. `aria-hidden`: the row beside
     * it already carries the file's name, and "the preview is not ready" is not news a
     * screen reader needs read out.
     */
    return (
      <div className={cx(TILE, className)} aria-hidden="true">
        <Spinner />
      </div>
    );
  }

  if (failed) {
    return <div className={cx(TILE, className)}>This image could not be loaded.</div>;
  }

  return (
    <img
      src={client.files.previewUrl(item.fileId)}
      alt={item.label}
      loading="lazy"
      onError={() => setFailed(true)}
      className={cx('rounded-control border border-line bg-surface object-contain', className)}
    />
  );
}
